import classnames from 'classnames'
import {css} from 'emotion'
import React from 'react'
import type {TGameId, TPlayerId} from '@kapitalist/contract/types'
import {unreachableCase} from '@kapitalist/utils/exhaustive'
import * as NEL from '@kapitalist/utils/NEList'
import {safely} from '@kapitalist/utils/safely'
import * as S from '@kapitalist/utils/Set'
import Gravatar from '~/components/Gravatar/stateless'
import {ToggleForGroup} from '~/components/Toggle'
import type {IToggleGroupRenderProps} from '~/components/ToggleGroup'
import ToggleGroup from '~/components/ToggleGroup'
import {px} from '~/components/utils'
import {DSTextDiv} from '~/design-system/DSText'
import type {TSkin} from '~/game/skins'
import {movesLeftForPlay} from '~/game/types'
import DualPropertyCard from '~/models/game/Cards/DualPropertyCard'
import type ResidenceCard from '~/models/game/Cards/ResidenceCard'
import type GamePlayer from '~/models/game/GamePlayer'
import type GamePosition from '~/models/game/GamePosition'
import type PortfolioNeighborhood from '~/models/game/PortfolioNeighborhood'
import type {
  AnyCard,
  AnyLiquidatableCard,
  AnyPropertyCard,
} from '~/models/game/types'
import {
  stackedCardGeometry,
  CASH_PILE_WIDTH_SCALE,
  cardStackWrapperGeometry,
} from '~/pages/GameLobbyV2/cardGeometry'
import type {TCardVisualState} from '~/pages/GameLobbyV2/cardVisualState'
import {cardVisualStates} from '~/pages/GameLobbyV2/cardVisualState'
import type {TClickableCardStateV2} from '~/pages/GameLobbyV2/clickableCardState'
import {
  clickableNeighborhoodIdsFor,
  clickablePlayerIdsFor,
  selectedNeighborhoodIdFor,
  selectedPlayerIdFor,
} from '~/pages/GameLobbyV2/clickableCardState'
import {V2LobbyCardImage} from '~/pages/GameLobbyV2/utils'
import styles from './styles.module.css'

type TPlayerPortfolioUIV2Orientation =
  | 'singleFileBottom'
  | 'singleFileTop'
  | 'stackedStageLeft'
  | 'stackedStageRight'
const classNamesForOrientation = (
  orientation: TPlayerPortfolioUIV2Orientation
): string => {
  switch (orientation) {
    case 'singleFileTop':
    case 'singleFileBottom':
      return classnames(styles.singleFile, styles[orientation])
    case 'stackedStageLeft':
    case 'stackedStageRight':
      return classnames(styles.stacked, styles[orientation])
    default:
      return unreachableCase(orientation)
  }
}

export interface IPlayerPortfolioUIV2Props {
  orientation: TPlayerPortfolioUIV2Orientation

  player: GamePlayer
  gameId: TGameId
  currentGamePosition: GamePosition
  currentPlayerId: TPlayerId

  skin: TSkin
  cardWidthPx: number
  className?: string

  clickableCardState: TClickableCardStateV2

  onCardClicked: (card: AnyCard, event: React.MouseEvent<any>) => void
  onPlayerClicked: (player: GamePlayer, event: React.MouseEvent<any>) => void
  onNeighborhoodClicked: (neighborhoodId: string) => void
}

interface IState {}

export default class PlayerPortfolioV2UI extends React.Component<
  IPlayerPortfolioUIV2Props,
  IState
> {
  state: IState = {}

  render() {
    const {
      orientation,
      player,
      currentPlayerId,
      currentGamePosition,
      skin,
      cardWidthPx,
      onCardClicked,
      onPlayerClicked,
      onNeighborhoodClicked,
      clickableCardState,
    } = this.props
    const portfolio = player.portfolio

    const isMyMajorTurn = currentGamePosition.majorActorPlayerId === player.id
    const isMyMinorTurn = currentGamePosition.minorActorPlayer?.id === player.id

    const movesLeft = movesLeftForPlay[currentGamePosition.play]
    const isMajorActionInProgress = !!currentGamePosition.inProgressMajorAction

    const visualStateFor = cardVisualStates(clickableCardState, currentGamePosition)

    const clickableNeighborhoodIds = clickableNeighborhoodIdsFor(clickableCardState)
    const selectedNeighborhoodId = selectedNeighborhoodIdFor(clickableCardState)

    const clickablePlayerIds = clickablePlayerIdsFor(clickableCardState)
    const selectedPlayerId = selectedPlayerIdFor(clickableCardState)

    const showToggleForPNId = (pnId: string) => clickableNeighborhoodIds.has(pnId)

    const orientationClassNames = classNamesForOrientation(orientation)
    const isCurrentPlayer = player.id === currentPlayerId
    const hideGravatar = false // orientation === 'singleFileBottom' && isCurrentPlayer

    return (
      <div
        className={classnames(
          styles.playerPortfolioWrapper,
          styles[
            currentGamePosition.players.length > 4
              ? 'moreThanFourPlayers'
              : 'fourPlayersOrLess'
          ],
          orientationClassNames,
          isCurrentPlayer && styles.isCurrentPlayer,
          this.props.className
        )}
      >
        {!hideGravatar && (
          <div
            className={classnames(
              styles.gravatarWrapper,
              clickablePlayerIds.has(player.id) && styles.clickable,
              selectedPlayerId === player.id && styles.selected,
              isMyMajorTurn && styles.isMyMajorTurn,
              isMyMinorTurn && styles.isMyMinorTurn
            )}
            onClick={e =>
              clickablePlayerIds.has(player.id) && onPlayerClicked(player, e)
            }
          >
            {isMyMajorTurn && (
              <div className={styles.turnArcs}>
                <img
                  alt=""
                  src="/icons/arc-60deg.svg"
                  className={classnames(
                    styles.turnArc,
                    styles.turnArc1,
                    movesLeft <= 0 && styles.faded,
                    movesLeft === 0 && isMajorActionInProgress && styles.blink
                  )}
                />
                <img
                  alt=""
                  src="/icons/arc-60deg.svg"
                  className={classnames(
                    styles.turnArc,
                    styles.turnArc2,
                    movesLeft <= 1 && styles.faded,
                    movesLeft === 1 && isMajorActionInProgress && styles.blink
                  )}
                />
                <img
                  alt=""
                  src="/icons/arc-60deg.svg"
                  className={classnames(
                    styles.turnArc,
                    styles.turnArc3,
                    movesLeft <= 2 && styles.faded,
                    movesLeft === 2 && isMajorActionInProgress && styles.blink
                  )}
                />
              </div>
            )}
            <Gravatar className={styles.gravatar} email={player.email} size={32} />
            <DSTextDiv size="label-demi" color="white">
              {player.username}
            </DSTextDiv>
            <DSTextDiv size="tiny" color="white" className={styles.cardCount}>
              <img alt="" src="/icons/masked-cards-small-no-fill.svg" />
              {player.hand.cardCount()}
            </DSTextDiv>
          </div>
        )}
        <div
          className={classnames(
            styles.playerNeighborhoodsWrapper,
            orientationClassNames
          )}
        >
          <ToggleGroup
            selectionLimit={1}
            allowDeselection={true}
            deselectOldestIfLimitReached
            selected={S.createOrEmpty(selectedNeighborhoodId)}
            makeSelection={selectedSet =>
              // TODO this used to assert `first()!`, I removed that in a type safety pass without testing.
              //  Check this out next time you see this - is anything upset if we don't call `onNeighborhoodClicked` when deselecting?
              safely(S.first(selectedSet), first => onNeighborhoodClicked(first))
            }
          >
            {(groupProps: IToggleGroupRenderProps<string>) => {
              return portfolio.sortedNeighborhoods().map(pn => (
                <ToggleForGroup
                  stopPropagationFromLabelClicks
                  toggleValue={pn.id}
                  groupProps={groupProps}
                  className={classnames(
                    styles.neighborhoodToggle,
                    orientationClassNames,
                    !showToggleForPNId(pn.id) && styles.hiddenToggle
                  )}
                  key={pn.id}
                  toggleStyle="radio"
                  disabled={!showToggleForPNId(pn.id)}
                >
                  {input => (
                    <SingleNeighborhood
                      key={pn.id}
                      model={pn}
                      skin={skin}
                      cardWidthPx={cardWidthPx}
                      visualStateFor={visualStateFor}
                      onCardClicked={onCardClicked}
                    >
                      {/* TODO unclear if this being a child of the neighborhood is really necessary or beneficial */}
                      {input}
                    </SingleNeighborhood>
                  )}
                </ToggleForGroup>
              ))
            }}
          </ToggleGroup>
        </div>
        <div className={styles.playerCashPileWrapper}>
          {portfolio.sortedCashPile().map(cashPileSegment => (
            <CashPile
              key={cashPileSegment[0].value}
              cashPileSegment={cashPileSegment}
              skin={skin}
              cardWidthPx={cardWidthPx * CASH_PILE_WIDTH_SCALE}
              visualStateFor={visualStateFor}
              onCardClicked={onCardClicked}
            />
          ))}
        </div>
      </div>
    )
  }
}

interface ISingleNeighborhoodProps {
  model: PortfolioNeighborhood
  skin: TSkin
  cardWidthPx: number

  visualStateFor: (cardId: string) => TCardVisualState
  onCardClicked: (card: AnyCard, event: React.MouseEvent<any>) => void

  children?: React.ReactNode
}

const SingleNeighborhood = (props: ISingleNeighborhoodProps) => {
  const {cardWidthPx, model, skin} = props

  const properties = NEL.createSafe(model.sortedProperties())
  if (!properties) {
    console.warn(
      'Not rendering an empty portfolio neighborhood:',
      model.neighborhood,
      model
    )
    return null
  }

  const cardsToRender: NEL.NEList<AnyPropertyCard | ResidenceCard> = [
    ...properties,
    ...model.residences,
  ]

  const wrapper = cardStackWrapperGeometry(cardWidthPx, cardsToRender.length)

  return (
    <div
      className={classnames(
        styles.neighborhoodWrapper,
        styles[`neighborhood-${model.neighborhood}`]
      )}
      style={{
        width: px(wrapper.width),
        height: px(wrapper.height),
        margin: px(wrapper.margin),
      }}
    >
      {cardsToRender.map((p, i) => {
        const slot = stackedCardGeometry(cardWidthPx, i)
        const visualState = props.visualStateFor(p.id)
        return (
          <V2LobbyCardImage
            key={p.id}
            model={p}
            skin={skin}
            forceRoundedCornersOnSharpCornerSkins
            invert={
              p instanceof DualPropertyCard &&
              p.upsideDownForNeighborhood(model.neighborhood)
            }
            className={classnames(
              styles.cardImage,
              visualState.clickable && styles.clickable,
              visualState.selected && styles.selected,
              visualState.fadeUnclickable && styles.fadeUnclickable,
              visualState.highlighted && styles.highlighted,
              visualState.faded && styles.faded,
              css`
                width: ${slot.width}px;
                height: ${slot.height}px;
                left: ${slot.left}px;
                top: ${slot.top}px;
              `
            )}
            onClick={e => {
              visualState.clickable && props.onCardClicked(p, e)
            }}
          />
        )
      })}
      {props.children}
    </div>
  )
}

interface ICashPileProps {
  cashPileSegment: NEL.NEList<AnyLiquidatableCard>
  skin: TSkin
  className?: string
  cardWidthPx: number

  visualStateFor: (cardId: string) => TCardVisualState
  onCardClicked: (card: AnyCard, event: React.MouseEvent<any>) => void
}

const CashPile = (props: ICashPileProps) => {
  const {cardWidthPx, cashPileSegment, skin} = props

  if (cashPileSegment.length === 0) {
    return null
  }

  const wrapper = cardStackWrapperGeometry(cardWidthPx, cashPileSegment.length)

  return (
    <div
      className={styles.cashPileWrapper}
      style={{
        width: px(wrapper.width),
        height: px(wrapper.height),
        margin: px(wrapper.margin),
      }}
    >
      {cashPileSegment.map((card, i) => {
        const slot = stackedCardGeometry(cardWidthPx, i)
        const visualState = props.visualStateFor(card.id)
        return (
          <V2LobbyCardImage
            key={card.id}
            model={card}
            skin={skin}
            forceRoundedCornersOnSharpCornerSkins
            invert={false}
            className={classnames(
              styles.cardImage,
              visualState.clickable && styles.clickable,
              visualState.selected && styles.selected,
              visualState.fadeUnclickable && styles.fadeUnclickable,
              visualState.highlighted && styles.highlighted,
              visualState.faded && styles.faded,
              css`
                width: ${slot.width}px;
                height: ${slot.height}px;
                left: ${slot.left}px;
                top: ${slot.top}px;
              `
            )}
            onClick={e => {
              visualState.clickable && props.onCardClicked(card, e)
            }}
          />
        )
      })}
    </div>
  )
}
